import { Link } from 'react-router-dom';

interface PostItem {
  id: string;
  category: string;
  title: string;
  content: string;
  empathy_count: number;
  reply_count: number;
  created_at: string;
  profiles?: {
    nickname: string;
    user_type: string;
    partner_role?: string;
    avatar_url?: string;
  };
}

interface UrgentAlertTabProps {
  posts: PostItem[];
  loading: boolean;
}

const URGENT_KEYWORDS = ['つらい', '限界', 'もう無理', '死にたい', '消えたい', '助けて', '孤独', '誰もいない'];

function getCategoryStyle(category: string) {
  switch (category) {
    case 'つぶやき・モヤモヤ':
      return 'bg-rose-100 text-rose-700';
    case '教えて！ケアの知恵':
      return 'bg-amber-100 text-amber-700';
    case '保育園・社会参加':
      return 'bg-emerald-100 text-emerald-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function findMatchedKeywords(content: string, title: string): string[] {
  return URGENT_KEYWORDS.filter(kw => content.includes(kw) || title.includes(kw));
}

export default function UrgentAlertTab({ posts, loading }: UrgentAlertTabProps) {
  const urgentPosts = posts.filter(p =>
    URGENT_KEYWORDS.some(kw => p.content.includes(kw) || p.title.includes(kw))
  );

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
        <div className="animate-spin w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full mx-auto mb-3"></div>
        <p className="text-gray-500 text-sm">読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-rose-50 border-2 border-rose-200 rounded-2xl p-4 sm:p-5">
        <div className="flex items-start space-x-2 sm:space-x-3">
          <i className="ri-error-warning-line text-rose-600 text-xl sm:text-2xl flex-shrink-0 mt-0.5"></i>
          <div>
            <h3 className="font-bold text-rose-800 mb-1 text-sm sm:text-base">緊急アラートについて</h3>
            <p className="text-rose-700 text-xs sm:text-sm">
              心理的孤立や危機的状況を示すキーワードを含む投稿です。まず共感を示し、必要に応じて専門機関への連携を検討してください。
            </p>
          </div>
        </div>
      </div>

      {urgentPosts.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-12 text-center">
          <i className="ri-shield-check-line text-4xl sm:text-5xl text-emerald-400 mb-3"></i>
          <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-1">緊急アラートはありません</h3>
          <p className="text-gray-500 text-xs sm:text-sm">現在、緊急対応が必要な投稿は検知されていません</p>
        </div>
      ) : (
        urgentPosts.map(post => {
          const matched = findMatchedKeywords(post.content, post.title);
          return (
            <div key={post.id} className="bg-white border-2 border-rose-300 rounded-2xl shadow-md p-4 sm:p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2 flex-wrap gap-1">
                  <span className="px-2 sm:px-3 py-1 bg-rose-500 text-white rounded-full text-xs font-medium flex items-center space-x-1 animate-pulse">
                    <i className="ri-alarm-warning-line text-xs"></i>
                    <span>緊急</span>
                  </span>
                  <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getCategoryStyle(post.category)}`}>
                    {post.category}
                  </span>
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                  {new Date(post.created_at).toLocaleDateString('ja-JP')}
                </span>
              </div>

              <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-1">{post.title}</h3>
              <p className="text-gray-600 text-xs sm:text-sm mb-3">{post.content}</p>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 sm:p-3 mb-3">
                <p className="text-xs text-amber-800">
                  <strong>検知キーワード：</strong> {matched.map(kw => `「${kw}」`).join(' ')}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between pt-3 border-t border-gray-100 gap-2">
                <div className="flex items-center space-x-2">
                  {post.profiles?.avatar_url ? (
                    <img src={post.profiles.avatar_url} alt="" className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover" />
                  ) : (
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gray-200 flex items-center justify-center">
                      <i className="ri-user-smile-line text-gray-500 text-xs"></i>
                    </div>
                  )}
                  <span className="text-xs text-gray-600">{post.profiles?.nickname || '匿名'}</span>
                </div>
                <div className="flex space-x-2">
                  <button className="flex-1 sm:flex-none px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-xs sm:text-sm hover:bg-amber-200 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center space-x-1">
                    <i className="ri-phone-line text-xs sm:text-sm"></i>
                    <span>連携機関へ</span>
                  </button>
                  <Link
                    to={`/board/post/${post.id}`}
                    className="flex-1 sm:flex-none px-3 sm:px-4 py-1.5 bg-rose-500 text-white rounded-lg text-xs sm:text-sm hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap text-center"
                  >
                    優先対応
                  </Link>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
